import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { SkinMode } from 'tag/ts/tag/model/type/SkinMode';
import { IAviaPlugin } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPlugin';
import { AviaPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/AviaPlugin';

/**
 * @copyright © Paramount Streaming
 * @description VTG player default UI skin plugin for advanced player controls and overlays.
 *
 * **Activation Logic:**
 * - **Full Player Type**: Only activates for full player instances (not embedded/minimal)
 * - **Plugin Availability**: Requires VTG UI plugin to be loaded and available.
 *   On desktop the VTG UI plugin is opt-in (must be explicitly forced); on CTV it is
 *   never loaded, so this plugin is inactive there.
 *
 * **Note:** Mobile-web-only display overrides (live play-toggle hide, compact ad control
 * bar, VOD replay overlay) are not part of this plugin — they live in the standalone
 * `MobileWebUI` Tag-level plugin (`plugins/tag/plugin_mobile_web_ui/`), dynamically fetched
 * only on a mobile user agent so that logic never ships inside desktop Tag builds. This
 * plugin only gates whether the VTG UI plugin itself loads (PLPSCORE-389).
 */
export class PlayerUIPlugin extends AviaPlugin implements IAviaPlugin {
	namespaceKey = 'player_ui';

	// no NPM package used for this plugin
	package = undefined;

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'PlayerUIPlugin'
			}
		);
	}

	/**
	 * @description Determines whether the plugin should be instantiated.
	 *
	 * Requires both a full player type (not embedded or minimal) and a loaded VTG UI plugin.
	 *
	 * @returns `true` if both conditions are met
	 */
	shouldBeUsed() {
		const isFullPlayer = this?.tag?.model?.skinMode === SkinMode.AVIA_UI;
		const pluginExists = !this.utils.Type.isNullOrUndefined( this.loadedPlugin );

		return isFullPlayer && pluginExists;
	}

	/**
	 * @description Returns factory initialization params, extending the base with a poster image URL.
	 *
	 * @returns Factory params including `posterImageUrl` sourced from content API metadata
	 */
	getFactoryParams() {
		return {
			...super.getFactoryParams(),
			posterImageUrl: this?.tag?.model?.apiMetadata?.thumbnail || undefined
		};
	}
}